/**
 * Loads a .previs SQLite file and returns a TimelineProject.
 *
 * Structured as a set of focused helper functions called by loadPrevisFile():
 *   loadDocumentMetadata  — title, aspect ratio, sensor size
 *   loadScenesWithShots   — scenes + shots + animatic continuing-duration merge
 *   loadGenerationHistory  — history entries, input ref resolution, lookup maps
 *   resolveHistoryToShots  — apply history thumbnails/video to shots, build VideoShot[]
 *   loadTransitions        — transitions with video from history or legacy BLOBs
 *   loadAudioClips         — audio clips
 *   loadComments           — comments
 *   loadApprovals          — approvals
 *   loadScreenplayData     — text + structured screenplay + characters + locations
 *   loadExtensionMeta      — review session ID, audio hints, voice assignments
 *   ensureDefaultApprovals — fill in pending approvals for uncovered shots
 */

import { openDatabase, closeDatabase } from './sqliteService.ts';
import { clearAllBlobs, stashBlob } from './audioBlobStore.ts';
import {
  aspectRatioToWeb,
  sensorSizeToWeb,
  locationTypeToWeb,
  timeOfDayToWeb,
  blobToDataUrl,
  transitionTypeToWeb,
  shotSizeToWeb,
  shotAngleToWeb,
  shotMovementToWeb,
  shotFramingToWeb,
} from './converters.ts';
import { createAssetUrl, cacheAssetBytes, clearAssetByteCache, clearPersistedHistoryTracking, markHistoryPersistedBatch } from './assetStorage.ts';
import { enqueueSendToNative } from '@/bridge/embeddedBridge.ts';

const DIAG_HISTORY_LOGGING = false;

function diagLog(msg: string): void {
  if (!DIAG_HISTORY_LOGGING) return;
  enqueueSendToNative('diagLog', { msg });
}
import type { TimelineProject, TimelineScene, TimelineShot, TimelineTransition, AudioClip, VideoShot, Comment, Approval, ScreenplayCharacter, ScreenplayLocation, ScreenplayDialogue, ScreenplayAction, ScreenplayTransitionElement, ScreenplayData, DocumentSettings, ShotCastMember, ShotCoverageLine } from '@/types/timeline.ts';
import type { GenerationHistoryEntry, GenerationAssetType } from '@/types/generationHistory.ts';
import type { VoiceAssignment } from '@/types/tts.ts';
import { normalizeApprovalStatus } from '@/types/timeline.ts';
import { sketchOverlayToAnnotation } from '@/bridge/sketchOverlayConverter.ts';
import { isGenericBlockObjectName } from './genericBlockObjectName.ts';
import { fullFrameFocalLengthFromAperture } from './cameraLens.ts';
import type { BaselinePayload, BaselineScene, BaselineElement } from '@/previs/screenplayNormalizer.ts';
import { computeSceneNumbers, computeShotNumbers } from '@/utils/numberingFormatter.ts';

// ─── Constants ─────────────────────────────────────────────────────────────

/** Scene color palette — auto-assigned by index */
const SCENE_COLORS = [
  '#3b6fa0', // steel blue
  '#8b5c3e', // warm brown
  '#3e7a5c', // forest green
  '#7a3e6a', // plum
  '#6a7a3e', // olive
  '#3e5a7a', // slate
  '#a05c3b', // rust
  '#5c3e8b', // deep purple
];

/** Audio lane color palette — indexed by lane number */
const AUDIO_LANE_COLORS = [
  '#4a90d9', // dialog — blue
  '#d94a8a', // music — pink
  '#4ad97a', // sfx — green
  '#d9a54a', // voiceover — amber
];

// ─── Utilities ─────────────────────────────────────────────────────────────

/** Generate a UUID (uses crypto.randomUUID when available, fallback for older browsers) */
function generateUUID(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback — sufficient for history IDs
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/** Check if a table exists in the database */
function tableExists(db: any, tableName: string): boolean {
  try {
    db.exec(`SELECT 1 FROM ${tableName} LIMIT 0`);
    return true;
  } catch {
    return false;
  }
}

/** Check if a column exists in a table */
function columnExists(db: any, tableName: string, columnName: string): boolean {
  try {
    const info = db.exec(`PRAGMA table_info(${tableName})`);
    if (info.length > 0) {
      return info[0].values.some((row: any[]) => row[1] === columnName);
    }
    return false;
  } catch {
    return false;
  }
}

// ─── Document Settings (Numbering) ────────────────────────────────────────

/** Load numbering-related settings from the documentsettings table */
/** Read a single integer column from documentsettings, returning fallback on missing column/table. */
function readSettingsInt(db: any, col: string, fallback: number): number {
  try {
    const r = db.exec(`SELECT ${col} FROM settings LIMIT 1`);
    if (r.length > 0 && r[0].values.length > 0) return (r[0].values[0][0] as number) ?? fallback;
  } catch { /* column or table missing */ }
  return fallback;
}

/** Read a single text column from documentsettings, returning fallback on missing column/table. */
function readSettingsText(db: any, col: string, fallback: string): string {
  try {
    const r = db.exec(`SELECT ${col} FROM settings LIMIT 1`);
    if (r.length > 0 && r[0].values.length > 0) return (r[0].values[0][0] as string) || fallback;
  } catch { /* column or table missing */ }
  return fallback;
}

/** Read the project's frame rate (24/25/30) from settings.frameratetype.
 *  Unity stores enum names — "FrameRate24_00" etc. Defaults to 30 (Unity's
 *  DocumentFrameRate.DefaultFrameRateType) when the row/column is missing. */
function loadProjectFrameRate(db: any): number {
  const raw = readSettingsText(db, 'frameratetype', '');
  switch (raw) {
    case 'FrameRate24_00': return 24;
    case 'FrameRate25_00': return 25;
    case 'FrameRate30_00': return 30;
    default: return 30;
  }
}

function loadDocumentSettings(db: any): DocumentSettings | undefined {
  // Check table exists at all
  try {
    const check = db.exec(`SELECT COUNT(*) FROM settings`);
    if (check.length === 0 || (check[0].values[0][0] as number) === 0) {
      enqueueSendToNative('diagLog', { msg: '[previsLoader] documentsettings table empty or missing rows' });
      return undefined;
    }
  } catch (e) {
    enqueueSendToNative('diagLog', { msg: `[previsLoader] documentsettings table does not exist: ${e}` });
    return undefined;
  }

  // Read each column individually — older .previs files may be missing newer columns
  const isLocked = readSettingsInt(db, 'isnumberinglocked', 0) === 1;
  const sepType = readSettingsText(db, 'sceneshotseparatortype', 'Nothing');
  const separator = sepType === 'Period' ? '.' : sepType === 'Hyphen' ? '-' : '';
  const useDigits = readSettingsInt(db, 'usedigitsforshotnumber', 0) === 1;
  const useFullAlphabet = readSettingsInt(db, 'usealphabetforshotnumbering', 0) === 1;
  const useStarting = readSettingsInt(db, 'usestartingscenenumber', 0) === 1;
  const startingNum = useStarting ? (readSettingsInt(db, 'startingscenenumber', 1) || 1) : 1;
  const sceneNumType = readSettingsText(db, 'scenenumberingtype', 'LetterNumber');
  const shotNumType = readSettingsText(db, 'shotnumberingtype', 'LetterNumber');

  enqueueSendToNative('diagLog', { msg: `[previsLoader] documentSettings loaded: isLocked=${isLocked}, useDigits=${useDigits}, useFullAlphabet=${useFullAlphabet}, separator='${separator}', startingNum=${startingNum}` });

  return {
    isNumberingLocked: isLocked,
    separator,
    useDigitsForShots: useDigits,
    useFullAlphabet,
    startingSceneNumber: startingNum,
    sceneNumberingType: sceneNumType,
    shotNumberingType: shotNumType,
  };
}

// ─── Schema Migration ──────────────────────────────────────────────────────

/**
 * Run migration-on-load: create history table if needed, migrate old BLOBs.
 * This mutates the in-memory db so migrations are persisted on next save.
 */
function migrateToHistoryTable(db: any, _scenes: TimelineScene[]): void {
  db.run('BEGIN');
  try {
    // Create history table if it doesn't exist
    if (!tableExists(db, 'timeline_generation_history')) {
      db.run(`CREATE TABLE IF NOT EXISTS timeline_generation_history (
        id            TEXT PRIMARY KEY,
        asset_type    TEXT NOT NULL,
        shot_id       INTEGER,
        transition_id INTEGER,
        model_id      TEXT NOT NULL,
        prompt        TEXT NOT NULL,
        seed          INTEGER,
        cost_credits  INTEGER NOT NULL,
        created_at    TEXT NOT NULL,
        asset_data    BLOB,
        asset_path    TEXT,
        input_data    BLOB,
        input_path    TEXT,
        mime_type     TEXT NOT NULL,
        metadata      TEXT,
        input_ref_history_id TEXT
      )`);
      db.run('CREATE INDEX IF NOT EXISTS idx_gen_history_shot ON timeline_generation_history(shot_id)');
      db.run('CREATE INDEX IF NOT EXISTS idx_gen_history_transition ON timeline_generation_history(transition_id)');
    }

    // Add input_ref_history_id column if missing
    if (!columnExists(db, 'timeline_generation_history', 'input_ref_history_id')) {
      try { db.run('ALTER TABLE timeline_generation_history ADD COLUMN input_ref_history_id TEXT'); } catch { /* already exists */ }
    }

    // Add history_id column to shots table
    if (!columnExists(db, 'shots', 'history_id')) {
      try { db.run('ALTER TABLE shots ADD COLUMN history_id TEXT'); } catch { /* already exists */ }
    }

    // Check if timeline_videoshots needs migration (has videodata but no history_id)
    if (tableExists(db, 'timeline_videoshots') && !columnExists(db, 'timeline_videoshots', 'history_id')) {
      // Migrate video BLOBs → history entries
      const videoRows = db.exec('SELECT videoshotid, shotid, videodata, mimetype FROM timeline_videoshots');
      if (videoRows.length > 0) {
        for (const row of videoRows[0].values) {
          const videoData = row[2] as Uint8Array | null;
          const mimeType = (row[3] as string) || 'video/mp4';
          if (videoData && videoData.length > 0) {
            const histId = generateUUID();
            db.run(
              `INSERT INTO timeline_generation_history
               (id, asset_type, shot_id, model_id, prompt, cost_credits, created_at, asset_data, mime_type)
               VALUES (?, 'video', ?, 'unknown', '(migrated)', 0, ?, ?, ?)`,
              [histId, row[1], new Date().toISOString(), videoData, mimeType]
            );
          }
        }
      }
      // Add history_id column
      try { db.run('ALTER TABLE timeline_videoshots ADD COLUMN history_id TEXT'); } catch { /* already exists */ }
    }

    // Migrate transition videos
    if (tableExists(db, 'timeline_transitions') && !columnExists(db, 'timeline_transitions', 'video_history_id')) {
      // Check if table has videodata column
      if (columnExists(db, 'timeline_transitions', 'videodata')) {
        const transRows = db.exec('SELECT transitionid, videodata, videomimetype FROM timeline_transitions WHERE videodata IS NOT NULL');
        if (transRows.length > 0) {
          for (const row of transRows[0].values) {
            const videoData = row[1] as Uint8Array | null;
            const mimeType = (row[2] as string) || 'video/mp4';
            if (videoData && videoData.length > 0) {
              const histId = generateUUID();
              db.run(
                `INSERT INTO timeline_generation_history
                 (id, asset_type, transition_id, model_id, prompt, cost_credits, created_at, asset_data, mime_type)
                 VALUES (?, 'transition_video', ?, 'unknown', '(migrated)', 0, ?, ?, ?)`,
                [histId, row[0], new Date().toISOString(), videoData, mimeType]
              );
            }
          }
        }
      }
      try { db.run('ALTER TABLE timeline_transitions ADD COLUMN video_history_id TEXT'); } catch { /* already exists */ }
    }

    // Migrate AI enhancements → generation history + set shots.history_id
    if (tableExists(db, 'timeline_ai_enhancements') && !columnExists(db, 'timeline_ai_enhancements', 'history_id')) {
      const enhRows = db.exec(
        'SELECT enhancementid, shotid, originalimage, enhancedimage, prompt, provider, createdat, tokencost, seed FROM timeline_ai_enhancements'
      );
      if (enhRows.length > 0) {
        for (const row of enhRows[0].values) {
          const shotId = row[1] as number;
          const originalBlob = row[2] as Uint8Array | null;
          const enhancedBlob = row[3] as Uint8Array | null;
          if (enhancedBlob && enhancedBlob.length > 0) {
            const histId = generateUUID();
            db.run(
              `INSERT INTO timeline_generation_history
               (id, asset_type, shot_id, model_id, prompt, cost_credits, created_at,
                asset_data, input_data, mime_type, seed)
               VALUES (?, 'image', ?, ?, ?, ?, ?, ?, ?, 'image/jpeg', ?)`,
              [
                histId, shotId,
                (row[5] as string) || 'unknown',
                (row[4] as string) || '',
                (row[7] as number) || 0,
                (row[6] as string) || new Date().toISOString(),
                enhancedBlob, originalBlob,
                (row[8] as number) || null,
              ]
            );
            // Set history_id on the shot so it's linked to this generation
            db.run('UPDATE shots SET history_id = ? WHERE shotid = ?', [histId, shotId]);
          }
        }
      }
      try { db.run('ALTER TABLE timeline_ai_enhancements ADD COLUMN history_id TEXT'); } catch { /* already exists */ }
    }

    db.run('COMMIT');
  } catch (err) {
    db.run('ROLLBACK');
    console.error('Migration failed, rolled back:', err);
  }
}

// ─── Data Loading Helpers ──────────────────────────────────────────────────

/** Load document title, aspect ratio, and sensor size from the document table */
/** Core Data epoch offset: seconds between 1970-01-01 and 2001-01-01 */
const CORE_DATA_EPOCH_OFFSET = 978307200;

function loadDocumentMetadata(db: any): { title: string; aspectRatio: string; sensorSize: string; lastModified?: string } {
  // Check if modificationtime column exists
  const hasModTime = columnExists(db, 'document', 'modificationtime');
  const cols = hasModTime
    ? 'title, aspectratiotype, sensorsizetype, modificationtime'
    : 'title, aspectratiotype, sensorsizetype';
  const docResult = db.exec(`SELECT ${cols} FROM document LIMIT 1`);

  let title = 'Untitled Project';
  let aspectRatio = '16:9';
  let sensorSize = 'Full Frame 35mm';
  let lastModified: string | undefined;

  if (docResult.length > 0 && docResult[0].values.length > 0) {
    const row = docResult[0].values[0];
    title = (row[0] as string) || title;
    aspectRatio = aspectRatioToWeb((row[1] as string) || '');
    sensorSize = sensorSizeToWeb((row[2] as string) || '');

    if (hasModTime && row[3] != null) {
      const coreDataSeconds = row[3] as number;
      const unixMs = (coreDataSeconds + CORE_DATA_EPOCH_OFFSET) * 1000;
      lastModified = new Date(unixMs).toISOString();
    }
  }

  return { title, aspectRatio, sensorSize, lastModified };
}

/** The file's schema version from `documentversion.fileversion`, or undefined when the table is
 *  unreadable. Every real .previs has carried this table since v1, so undefined in practice means
 *  the embedded slim DB (EmbeddedSlimDatabaseBuilder omits `documentversion` from its whitelist) —
 *  a file Unity has always migrated before pushing. Consumers therefore treat undefined as
 *  "current schema", not "ancient file". */
export function loadFileVersion(db: any): number | undefined {
  try {
    const result = db.exec('SELECT fileversion FROM documentversion LIMIT 1');
    if (result.length === 0 || result[0].values.length === 0) return undefined;
    const raw = result[0].values[0][0];
    if (raw == null) return undefined;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
}

/** Load scenes with their shots from the scenes/shots/cameraobjects tables.
 *  Also returns revertedShotIds — shots whose history_id was explicitly cleared
 *  (empty string in DB, set by Unity's revertShotToLive), so the video fallback
 *  in resolveHistoryToShots doesn't re-attach reverted assets.
 */
export function loadScenesWithShots(db: any): { scenes: TimelineScene[]; revertedShotIds: Set<number> } {
  // Load all non-hidden scenes, INCLUDING omitted ones. Omitted scenes are now
  // loaded (Slice B) so the Compact board can gray them and numbering counts
  // them for Unity parity; consumers (exports/playback/pickers) filter them out
  // via scene.isOmitted. Hidden scenes stay lifecycle-flagged "not in the active
  // script" and are still excluded here.
  const scenesResult = db.exec(
    'SELECT sceneid, number, name, locationtype, timetype, isomitted FROM scenes ' +
    'WHERE ishidden = 0 ORDER BY sceneorder'
  );

  const scenes: TimelineScene[] = [];
  const revertedShotIds = new Set<number>();
  const hasHistoryIdCol = columnExists(db, 'shots', 'history_id');
  const hasCoverageColorCol = columnExists(db, 'shots', 'coverage_color');
  // Forward-defense for a future slim DB that drops the column entirely (today's slim
  // DBs still ship it, empty-valued — see sqliteBridge.ts's merge-contract comments).
  const hasStaticImageDataCol = columnExists(db, 'shots', 'staticimagedata');
  // 20260922 - THE REAL LENS. `cameraobjects.apertureangle` is the only field the camera record
  // layer actually persists; `focallength` is a dead DEFAULT(10) column (see previs/cameraLens.ts).
  // A Version 0001 column, present in every real .previs and copied verbatim into the slim DB
  // (EmbeddedSlimDatabaseBuilder has no override for cameraobjects) — guarded anyway, the way every
  // other column in this SELECT is, because a shape this query does not expect must fall back to the
  // old read rather than throw "no such column" and kill the whole project load.
  const hasApertureAngleCol = columnExists(db, 'cameraobjects', 'apertureangle');

  // 20260902 - PLACEHOLDER PROPS (labeled crate stand-ins). The blocking solver stands in for a prop
  // it could not resolve with a near-black box carrying a gold dashed edge and the prop's name. Unity
  // decides "still a placeholder" (SceneBlockManager.IsPlaceholderCrateBlock) on the block's
  // EFFECTIVE identity, and that word is load-bearing: since 20260731 a solver placeholder is LINKED
  // to a project-wide `blocks` library row (so one replacement propagates to every shot), and the
  // instance row's own name/type columns then describe nothing — the library row's do. So the name
  // and type resolve through `blocks` when `blockid > 0` and come from the instance row otherwise
  // (-1 is the record layer's "no library block"); the type must be the default 'Cube', the name
  // must be non-blank, and the name must not be the app's generic unnamed-object convention
  // (isGenericBlockObjectName — "Object 13", legacy "Block"). A linked row whose library row is gone
  // resolves to NULL and drops out, as Unity renders nothing for it. This is the ONLY thing the web
  // ever reads out of `blockobjects` / `blocks`: an Enhance render of such a shot has to be told to
  // replace them, which is promptBuilder's PLACEHOLDER_PROPS_CLAUSE.
  //
  // ONE query for the whole file, hoisted above the scene loop: `blockobjects` is per-shot and runs
  // to thousands of rows in a real project, so a per-scene query would re-scan it once per scene for
  // a single boolean.
  //
  // Table-guarded the way screenplay_coverage_lines is below. `blockobjects` and `blocks` only
  // entered the embedded slim DB's whitelist on 20260902 (EmbeddedSlimDatabaseBuilder), so a slim
  // DB written by an older Unity simply has no such table and every shot must come back with the
  // flag ABSENT rather than the whole project load dying on "no such table"; a file with the
  // instances but not the library can still recognise an UNLINKED crate. `ishidden` is guarded
  // separately: it arrived in blockobjects Version 0015, and a file old enough to lack it has no
  // hidden blocks to exclude anyway.
  const placeholderPropShotIds = new Set<number>();
  if (tableExists(db, 'blockobjects')) {
    try {
      const hiddenTerm = columnExists(db, 'blockobjects', 'ishidden') ? ' AND bo.ishidden = 0' : '';
      const hasLibrary = tableExists(db, 'blocks');
      const effName = hasLibrary ? 'CASE WHEN bo.blockid > 0 THEN b.blockname ELSE bo.blockname END' : 'bo.blockname';
      const effType = hasLibrary ? 'CASE WHEN bo.blockid > 0 THEN b.blocktype ELSE bo.blocktype END' : 'bo.blocktype';
      const join = hasLibrary ? 'LEFT JOIN blocks b ON b.blockid = bo.blockid' : '';
      const linkTerm = hasLibrary ? '' : ' AND bo.blockid <= 0';
      const ppResult = db.exec(
        `SELECT DISTINCT bo.shotid, ${effName} AS effname FROM blockobjects bo ${join}
         WHERE ${effType} = 'Cube' AND TRIM(COALESCE(${effName}, '')) <> ''${linkTerm}${hiddenTerm}`
      );
      if (ppResult.length > 0) {
        for (const row of ppResult[0].values) {
          if (!isGenericBlockObjectName(row[1] as string)) placeholderPropShotIds.add(row[0] as number);
        }
      }
    } catch { /* unexpected blockobjects/blocks shape — an un-flagged prompt beats a dead load */ }
  }

  if (scenesResult.length > 0) {
    for (let i = 0; i < scenesResult[0].values.length; i++) {
      const sceneRow = scenesResult[0].values[i];
      const sceneId = sceneRow[0] as number;

      // ── Shots for this scene ─────────────────────────────────────
      // Exclude Continuing shots (folded into their Initial parent) and
      // hidden shots.
      // Build shot query with optional columns
      // Column layout: 0-shotid 1-sceneid 2-number 3-name 4-shotorder 5-duration
      //   6-staticimagedata 7-notes 8-focallength 9-fstop
      //   10-shotsizetype 11-shotangletype 12-shotmovementtype
      //   13-hasstaticimage 14-isstaticimageenabled 15-ispostershot 16-shotframingtype
      //   17+ optional, in this order: history_id, coverage_color, apertureangle
      //   then shotcategorytype (ALWAYS LAST)
      // New columns go on the END of extraCols (in front of shotcategorytype), never into the fixed
      // 0-16 block — every index below 17 is hardcoded at the read site.
      const extraCols = [
        's.shotframingtype',
        ...(hasHistoryIdCol ? ['s.history_id'] : []),
        ...(hasCoverageColorCol ? ['s.coverage_color'] : []),
        ...(hasApertureAngleCol ? ['c.apertureangle'] : []),
        's.shotcategorytype',
      ];
      // Guard staticimagedata the same way (columnExists, NULL fallback) so a slim DB that
      // drops the column outright doesn't throw "no such column" and kill the whole load.
      const staticImageDataCol = hasStaticImageDataCol ? 's.staticimagedata' : 'NULL AS staticimagedata';
      const shotQuery = `SELECT s.shotid, s.sceneid, s.number, s.name, s.shotorder, s.duration,
                  ${staticImageDataCol}, s.notes,
                  c.focallength, c.fstop,
                  s.shotsizetype, s.shotangletype, s.shotmovementtype,
                  s.hasstaticimage, s.isstaticimageenabled, s.ispostershot,
                  ${extraCols.join(', ')}
           FROM shots s
           LEFT JOIN cameraobjects c ON c.shotid = s.shotid AND c.isenabled = 1
           WHERE s.sceneid = ? AND s.ishidden = 0 AND s.shotcategorytype != 'Continuing'
           ORDER BY s.shotorder`;
      const shotsResult = db.exec(shotQuery, [sceneId]);

      // For animatic (Initial) shots, sum continuing shot durations into the
      // parent — excluding the last continuing shot which is the endpoint pose
      // (the animatics system forces its duration to 0 during playback).
      const continuingDurations = new Map<number, number>();
      try {
        const contResult = db.exec(
          `SELECT initialshotid, duration FROM shots
           WHERE sceneid = ? AND shotcategorytype = 'Continuing' AND ishidden = 0
           ORDER BY initialshotid, keyframeshotorder`,
          [sceneId]
        );
        if (contResult.length > 0) {
          // Group by initialshotid, sum all but last per group
          const groups = new Map<number, number[]>();
          for (const contRow of contResult[0].values) {
            const parentId = contRow[0] as number;
            const dur = (contRow[1] as number) || 0;
            const list = groups.get(parentId);
            if (list) list.push(dur);
            else groups.set(parentId, [dur]);
          }
          for (const [parentId, durations] of groups) {
            // Exclude last (endpoint pose)
            let sum = 0;
            for (let ci = 0; ci < durations.length - 1; ci++) {
              sum += durations[ci];
            }
            continuingDurations.set(parentId, sum);
          }
        }
      } catch { /* column missing in older files — no animatics */ }

      // ── Cast per shot (characterobjects joined to characters) ────
      const castByShot = new Map<number, ShotCastMember[]>();
      try {
        const castResult = db.exec(
          `SELECT co.shotid, c.name AS char_name, co.charactertype, co.gendertype, co.expressiontype
           FROM characterobjects co
           LEFT JOIN characters c ON c.characterid = co.characterid
           WHERE co.shotid IN (SELECT shotid FROM shots WHERE sceneid = ? AND ishidden = 0)
             AND co.ishidden = 0 AND co.issetpiece = 0
           ORDER BY co.shotid`,
          [sceneId]
        );
        if (castResult.length > 0) {
          for (const row of castResult[0].values) {
            const sid = row[0] as number;
            const charNameRaw = (row[1] as string) || '';
            const gender = (row[3] as string) || '';
            const expr = (row[4] as string) || 'Neutral';
            // Named characters use their name; unnamed get "Man"/"Woman" from gender
            const name = charNameRaw
              || (gender === 'Female' ? 'Woman' : gender === 'Male' ? 'Man' : 'Figure');
            const member: ShotCastMember = {
              name,
              gender: gender && gender !== 'All' ? gender : undefined,
              expression: expr !== 'Neutral' ? expr : undefined,
            };
            const list = castByShot.get(sid);
            if (list) list.push(member);
            else castByShot.set(sid, [member]);
          }
        }
      } catch { /* characterobjects table missing in older files */ }

      const shots: TimelineShot[] = [];

      if (shotsResult.length > 0) {
        for (const shotRow of shotsResult[0].values) {
          const shotId = shotRow[0] as number;
          const staticImageData = shotRow[6] as Uint8Array | null;
          const thumbnailDataUrl = blobToDataUrl(staticImageData);

          // Dynamic column indices after the fixed columns (0..15)
          // 13 = hasstaticimage, 14 = isstaticimageenabled, 15 = ispostershot
          // 16 = shotframingtype (always present)
          // then optional: history_id, coverage_color, apertureangle
          // last = shotcategorytype
          const hasStaticImage = (shotRow[13] as number) === 1;
          const isStaticImageEnabled = (shotRow[14] as number) === 1;
          let ci = 17; // next index after shotframingtype
          const historyIdIdx = hasHistoryIdCol ? ci++ : -1;
          const coverageColorIdx = hasCoverageColorCol ? ci++ : -1;
          const apertureIdx = hasApertureAngleCol ? ci++ : -1;
          const categoryIdx = ci; // shotcategorytype is always last

          const shotCategory = (shotRow[categoryIdx] as string) || 'Normal';
          const isAnimatic = shotCategory === 'Initial';

          // For animatic shots, add continuing shot durations
          const ownDuration = (shotRow[5] as number) || 2.5;
          let duration = ownDuration;
          if (isAnimatic) {
            const extra = continuingDurations.get(shotId);
            if (extra) duration += extra;
          }

          shots.push({
            id: shotId,
            sceneId: shotRow[1] as number,
            number: (shotRow[2] as string) || '',
            // Preserve the RAW shot number ('' = auto, non-empty = manual override) before the numbering
            // pass below overwrites `number` with the computed display letter. reorderShotInScene
            // recomputes display from this so a drag doesn't clobber overrides / locked numbers.
            numberOverride: (shotRow[2] as string) || '',
            name: (shotRow[3] as string) || '',
            order: shotRow[4] as number,
            duration,
            // 20260922 - the UN-SUMMED `shots.duration`, carried ONLY for an animatic shot (whose
            // `duration` above is the Continuing run folded in). Anything that writes a duration back
            // writes this one column, so an editor seeded from the sum compounds it on every save —
            // see TimelineShot.ownDuration and seedReviewShotsFromScene. Set-only-when-animatic (a
            // spread, never `undefined`) so every other shot object still compares equal to one built
            // outside the loader, the same rule hasPlaceholderProps follows below.
            ...(isAnimatic ? { ownDuration } : {}),
            thumbnailDataUrl,
            hasStaticImage: hasStaticImage && isStaticImageEnabled,
            isPosterShot: (shotRow[15] as number) === 1,
            historyId: historyIdIdx >= 0 ? ((shotRow[historyIdIdx] as string) || undefined) : undefined,
            // The same column, kept RAW ('' when empty) so the orphan-clear in resolveHistoryToShots
            // stays distinguishable from a FK Unity really cleared. sqliteBridge's refresh merge is
            // the only reader — see TimelineShot.rawHistoryId.
            rawHistoryId: historyIdIdx >= 0 ? ((shotRow[historyIdIdx] as string) || '') : '',
            notes: (shotRow[7] as string) || undefined,
            // 20260922 - THE REAL LENS, derived from the render-authoritative `apertureangle`.
            // `c.focallength` (index 8) is a dead DEFAULT(10) column the camera record layer never
            // writes, so reading it made every hand-framed shot report 10mm — see previs/cameraLens.ts.
            // It stays as the FALLBACK for a camera row with no usable aperture at all (no enabled
            // camera on the shot, the column's own 0 default, or a DB shape without the column).
            focalLength: (apertureIdx >= 0 ? fullFrameFocalLengthFromAperture(shotRow[apertureIdx] as number | null) : undefined)
              ?? ((shotRow[8] as number) || 10),
            fStop: (shotRow[9] as number) || 5.6,
            shotSize: shotSizeToWeb(shotRow[10] as string | null),
            shotAngle: shotAngleToWeb(shotRow[11] as string | null),
            shotMovement: shotMovementToWeb(shotRow[12] as string | null),
            shotFraming: shotFramingToWeb(shotRow[16] as string | null),
            coverageColor: coverageColorIdx >= 0 ? ((shotRow[coverageColorIdx] as string) || undefined) : undefined,
            isAnimatic: isAnimatic ? true : undefined,
            cast: castByShot.get(shotId),
            // Spread rather than a ternary so the key is genuinely ABSENT, never `false` and never
            // present-but-undefined — see TimelineShot.hasPlaceholderProps.
            ...(placeholderPropShotIds.has(shotId) ? { hasPlaceholderProps: true } : {}),
          });

          // Detect explicitly-cleared history_id (empty string '' set by Unity's revertShotToLive)
          if (historyIdIdx >= 0 && (shotRow[historyIdIdx] as string) === '') {
            revertedShotIds.add(shotId);
          }
        }
      }

      scenes.push({
        id: sceneId,
        number: (sceneRow[1] as string) || '',
        // Preserve the RAW scene number ('' = auto, non-empty = manual override) before the
        // numbering pass below overwrites `number` with the computed display value. reorderScene
        // recomputes display from this so reordering doesn't clobber overrides / locked numbers.
        numberOverride: (sceneRow[1] as string) || '',
        name: (sceneRow[2] as string) || '',
        locationType: locationTypeToWeb((sceneRow[3] as string) || ''),
        timeOfDay: timeOfDayToWeb((sceneRow[4] as string) || ''),
        color: SCENE_COLORS[i % SCENE_COLORS.length],
        shots,
        // Column index 5 in the SELECT above. Preserved so consumers can filter and
        // the Compact board can gray this scene.
        isOmitted: (sceneRow[5] as number) === 1,
      });
    }
  }

  // v50 shots.coverage blob (optional column)
  try {
    const covResult = db.exec('SELECT shotid, coverage FROM shots');
    if (covResult.length > 0) {
      const covMap = new Map<number, string>();
      for (const row of covResult[0].values) {
        const c = (row[1] as string) || '';
        if (c) covMap.set(row[0] as number, c);
      }
      for (const scene of scenes) for (const shot of scene.shots) {
        const c = covMap.get(shot.id);
        if (c) shot.coverage = c;
      }
    }
  } catch { /* pre-v50 file */ }

  // v55 screenplay_coverage_lines — the board panel's Coverage sub-view dataset (several shots may
  // cover one element, each assignment carrying its own line style). Presence-guarded the way
  // hasCoverageColorCol guards the v43 column above: the table is simply absent from pre-v55 files
  // and from a slim DB built by an older Unity, and that must leave `coverageLines` undefined
  // rather than throw "no such table" and kill the whole load.
  if (tableExists(db, 'screenplay_coverage_lines')) {
    try {
      const clResult = db.exec(
        `SELECT shot_id, element_type, element_id, line_style, sort_order
         FROM screenplay_coverage_lines ORDER BY shot_id, sort_order`
      );
      if (clResult.length > 0) {
        const linesByShot = new Map<number, ShotCoverageLine[]>();
        for (const row of clResult[0].values) {
          // Only the two coverable types survive the trip. A transition row (or anything else that
          // slipped past Unity's allow-list) is dropped, never typed as one of the two.
          const elementType = (row[1] as string) || '';
          if (elementType !== 'dialogue' && elementType !== 'action') continue;
          const shotId = row[0] as number;
          const line: ShotCoverageLine = {
            elementType,
            elementId: row[2] as number,
            lineStyle: (row[3] as string) === 'wavy' ? 'wavy' : 'straight',
            sortOrder: (row[4] as number) || 0,
          };
          const list = linesByShot.get(shotId);
          if (list) list.push(line);
          else linesByShot.set(shotId, [line]);
        }
        // Rows belonging to a hidden or Continuing shot never find a home — those shots are filtered
        // out of `shots` by the query above — which is the same "orphan rows just don't render"
        // semantics the legacy shot_id lining has.
        for (const scene of scenes) for (const shot of scene.shots) {
          const lines = linesByShot.get(shot.id);
          if (lines) shot.coverageLines = lines;
        }
      }
    } catch { /* pre-v55 file */ }
  }

  // v50 scenes.rough_blocking blob (optional column, renamed from staging_cache) — hydrates the rough-block session cache.
  try {
    const rbResult = db.exec('SELECT sceneid, rough_blocking FROM scenes');
    if (rbResult.length > 0) {
      const rbMap = new Map<number, string>();
      for (const row of rbResult[0].values) {
        const c = (row[1] as string) || '';
        if (c) rbMap.set(row[0] as number, c);
      }
      for (const scene of scenes) {
        const c = rbMap.get(scene.id);
        if (c) scene.roughBlocking = c;
      }
    }
  } catch { /* pre-v50 file (or pre-rename staging_cache) */ }

  // v41 scenes.screenplay_location_id — authoritative scene→location FK as of the v52 backfill.
  try {
    const flkResult = db.exec('SELECT sceneid, screenplay_location_id FROM scenes');
    if (flkResult.length > 0) {
      const flkMap = new Map<number, number>();
      for (const row of flkResult[0].values) {
        const v = (row[1] as number) || 0;
        if (v > 0) flkMap.set(row[0] as number, v);
      }
      for (const scene of scenes) {
        const v = flkMap.get(scene.id);
        if (v) scene.screenplayLocationId = v;
      }
    }
  } catch { /* pre-v41 file */ }

  // v52 scenes.location_notes — per-scene location notes for the Locations tab.
  try {
    const lnResult = db.exec('SELECT sceneid, location_notes FROM scenes');
    if (lnResult.length > 0) {
      const lnMap = new Map<number, string>();
      for (const row of lnResult[0].values) {
        const c = (row[1] as string) || '';
        if (c) lnMap.set(row[0] as number, c);
      }
      for (const scene of scenes) {
        const c = lnMap.get(scene.id);
        if (c) scene.locationNotes = c;
      }
    }
  } catch { /* pre-v52 file */ }

  // v50 scenes.suggested_shots envelope (optional column) — persisted SuggestedShotsEnvelope JSON.
  try {
    const ssResult = db.exec('SELECT sceneid, suggested_shots FROM scenes');
    if (ssResult.length > 0) {
      const ssMap = new Map<number, string>();
      for (const row of ssResult[0].values) {
        const c = (row[1] as string) || '';
        if (c) ssMap.set(row[0] as number, c);
      }
      for (const scene of scenes) {
        const c = ssMap.get(scene.id);
        if (c) scene.suggestedShots = c;
      }
    }
  } catch { /* pre-v50 file */ }

  return { scenes, revertedShotIds };
}

/** Result type for loadGenerationHistory */
interface GenerationHistoryResult {
  entries: GenerationHistoryEntry[];
  byId: Map<string, GenerationHistoryEntry>;
  videoByShot: Map<number, GenerationHistoryEntry>;
  videoByTransition: Map<number, GenerationHistoryEntry>;
}

/** Load generation history entries, resolve input references, and build lookup maps */
function loadGenerationHistory(db: any): GenerationHistoryResult {
  const entries: GenerationHistoryEntry[] = [];
  const hasInputRefCol = columnExists(db, 'timeline_generation_history', 'input_ref_history_id');
  const hasClipIdCol = columnExists(db, 'timeline_generation_history', 'clip_id');
  try {
    const selectCols = hasInputRefCol
      ? (hasClipIdCol
        ? `id, asset_type, shot_id, transition_id, model_id, prompt, seed,
           cost_credits, created_at, asset_data, asset_path, input_data, input_path,
           mime_type, metadata, input_ref_history_id, clip_id`
        : `id, asset_type, shot_id, transition_id, model_id, prompt, seed,
           cost_credits, created_at, asset_data, asset_path, input_data, input_path,
           mime_type, metadata, input_ref_history_id`)
      : `id, asset_type, shot_id, transition_id, model_id, prompt, seed,
         cost_credits, created_at, asset_data, asset_path, input_data, input_path,
         mime_type, metadata`;
    const histResult = db.exec(
      `SELECT ${selectCols} FROM timeline_generation_history ORDER BY created_at`
    );
    if (histResult.length > 0) {
      for (const row of histResult[0].values) {
        const id = row[0] as string;
        const assetData = row[9] as Uint8Array | null;
        const inputData = row[11] as Uint8Array | null;
        const mimeType = (row[13] as string) || 'application/octet-stream';

        // Create Blob URLs from BLOBs
        let objectUrl = '';
        if (assetData && assetData.length > 0) {
          objectUrl = createAssetUrl(assetData, mimeType);
        }
        diagLog(`DIAG: loadGenerationHistory: id=${id}, assetType=${row[1]}, assetDataLen=${assetData?.length ?? 0}, hasObjectUrl=${!!objectUrl}`);

        let inputObjectUrl: string | null = null;
        if (inputData && inputData.length > 0) {
          inputObjectUrl = createAssetUrl(inputData, 'image/jpeg');
        }

        let metadataParsed: Record<string, unknown> | null = null;
        try {
          const metaStr = row[14] as string | null;
          if (metaStr) metadataParsed = JSON.parse(metaStr);
        } catch { /* invalid JSON — ignore */ }

        entries.push({
          id,
          assetType: (row[1] as string) as GenerationAssetType,
          shotId: (row[2] as number) || null,
          transitionId: (row[3] as number) || null,
          modelId: (row[4] as string) || 'unknown',
          prompt: (row[5] as string) || '',
          seed: (row[6] as number) || null,
          costCredits: (row[7] as number) || 0,
          createdAt: (row[8] as string) || new Date().toISOString(),
          objectUrl,
          assetPath: (row[10] as string) || null,
          inputObjectUrl,
          inputPath: (row[12] as string) || null,
          inputRefHistoryId: hasInputRefCol ? ((row[15] as string) || null) : null,
          clipId: (hasInputRefCol && hasClipIdCol) ? ((row[16] as number) || null) : null,
          mimeType,
          metadata: metadataParsed,
        });
      }
    }
  } catch {
    // Table doesn't exist — that's fine (shouldn't happen after migration)
  }

  // Resolve input references: if entry has inputRefHistoryId but no inputObjectUrl, copy from referenced entry
  const byId = new Map(entries.map(e => [e.id, e]));
  for (const entry of entries) {
    if (!entry.inputObjectUrl && entry.inputRefHistoryId) {
      const ref = byId.get(entry.inputRefHistoryId);
      if (ref?.inputObjectUrl) {
        entry.inputObjectUrl = ref.inputObjectUrl;
      }
    }
  }

  // Mark all loaded entries as persisted (they came from SQLite)
  if (entries.length > 0) {
    markHistoryPersistedBatch(entries.map(e => e.id));
  }

  // Build lookup maps for resolving FKs
  const videoByShot = new Map<number, GenerationHistoryEntry>();
  const videoByTransition = new Map<number, GenerationHistoryEntry>();
  for (const entry of entries) {
    if (entry.assetType === 'video' && entry.shotId != null) {
      // Take the latest video entry for each shot
      videoByShot.set(entry.shotId, entry);
    } else if (entry.assetType === 'transition_video' && entry.transitionId != null) {
      videoByTransition.set(entry.transitionId, entry);
    }
  }

  return { entries, byId, videoByShot, videoByTransition };
}

/** Apply history entries to shot thumbnails and build VideoShot array.
 *  Primary lookup: shot.historyId → historyById (works for both image & video,
 *  and correctly handles re-applied assets on different shots).
 *  Fallback: videoByShot (keyed by entry.shotId) for legacy files where
 *  shot.historyId might not be set for older video entries — so it fires only for shots that have
 *  no FK of their own. A shot whose FK names some other live entry had its video superseded (an
 *  image apply re-points the FK and drops the video), and re-attaching it here would resurrect the
 *  video on every cold load.
 *
 *  `suppressTileGraft` turns OFF the image branch's thumbnail assignment only —
 *  see loadPrevisFile's options for why embedded mode passes it.
 */
function resolveHistoryToShots(
  scenes: TimelineScene[],
  historyById: Map<string, GenerationHistoryEntry>,
  videoByShot: Map<number, GenerationHistoryEntry>,
  revertedShotIds: Set<number>,
  suppressTileGraft: boolean,
): VideoShot[] {
  const videoShots: VideoShot[] = [];

  // Primary: resolve via shot.historyId for both images and videos
  for (const scene of scenes) {
    for (const shot of scene.shots) {
      if (shot.historyId) {
        const histEntry = historyById.get(shot.historyId);
        diagLog(`DIAG: resolveHistoryToShots: shot=${shot.id}, historyId=${shot.historyId}, histEntryFound=${!!histEntry}, hasObjectUrl=${!!histEntry?.objectUrl}, assetType=${histEntry?.assetType}`);
        // Clear orphaned historyId — entry missing or has no usable asset data
        if (!histEntry || !histEntry.objectUrl) {
          diagLog(`DIAG: resolveHistoryToShots: clearing orphaned historyId on shot ${shot.id} (entry ${histEntry ? 'exists but no objectUrl' : 'missing'})`);
          shot.historyId = undefined;
        }
        if (histEntry?.objectUrl) {
          if (histEntry.assetType === 'video') {
            shot.videoObjectUrl = histEntry.objectUrl;
            videoShots.push({
              shotId: shot.id,
              objectUrl: histEntry.objectUrl,
              mimeType: histEntry.mimeType,
              historyId: histEntry.id,
            });
          } else if (!suppressTileGraft) {
            diagLog(`DIAG: resolveHistoryToShots: OVERWRITING shot ${shot.id} thumbnailDataUrl with history blob URL`);
            shot.thumbnailDataUrl = histEntry.objectUrl;
          }
        }
      }
    }
  }

  // Fallback: legacy entries where shot didn't have historyId set for videos.
  // Skip shots whose history_id was explicitly cleared by revert — they should
  // stay reverted, not get the video re-attached from history.
  // Also skip shots whose FK names a live entry of its own: the FK is the record of which medium won,
  // and an image apply re-points it at the image while deliberately dropping the video (actions.ts
  // applyBatchGenerations / acceptEnhancement). Only an absent FK is the legacy shape this exists for —
  // the primary loop above already cleared FKs that resolve to nothing.
  for (const [shotId, histEntry] of videoByShot) {
    if (videoShots.some(v => v.shotId === shotId) || !histEntry.objectUrl || revertedShotIds.has(shotId)) continue;

    let target: TimelineShot | undefined;
    for (const scene of scenes) {
      target = scene.shots.find(s => s.id === shotId);
      if (target) break;
    }
    if (target?.historyId) continue;

    videoShots.push({
      shotId,
      objectUrl: histEntry.objectUrl,
      mimeType: histEntry.mimeType,
      historyId: histEntry.id,
    });
    // Also set on shot
    if (target && !target.videoObjectUrl) {
      target.videoObjectUrl = histEntry.objectUrl;
    }
  }

  return videoShots;
}

/** Load transitions from the extension table, resolving video from history or legacy BLOBs */
function loadTransitions(
  db: any,
  videoByTransition: Map<number, GenerationHistoryEntry>,
): TimelineTransition[] {
  try {
    // Try with videodata columns first (newer files), fall back without
    let transResult;
    let hasVideoColumns = false;
    try {
      transResult = db.exec(
        `SELECT transitionid, fromshotid, toshotid, transitiontype, duration, easing, videodata, videomimetype, video_history_id
         FROM timeline_transitions ORDER BY transitionid`
      );
      hasVideoColumns = true;
    } catch {
      try {
        transResult = db.exec(
          `SELECT transitionid, fromshotid, toshotid, transitiontype, duration, easing, videodata, videomimetype
           FROM timeline_transitions ORDER BY transitionid`
        );
        hasVideoColumns = true;
      } catch {
        transResult = db.exec(
          `SELECT transitionid, fromshotid, toshotid, transitiontype, duration, easing
           FROM timeline_transitions ORDER BY transitionid`
        );
      }
    }
    if (transResult.length > 0) {
      return transResult[0].values.map((row: any[]) => {
        const transId = row[0] as number;
        const trans: TimelineTransition = {
          id: transId,
          fromShotId: row[1] as number,
          toShotId: row[2] as number,
          type: transitionTypeToWeb((row[3] as string) || ''),
          duration: (row[4] as number) || 1.0,
          easing: (row[5] as string) || 'linear',
        };

        // Resolve video from history if available
        const histEntry = videoByTransition.get(transId);
        if (histEntry && histEntry.objectUrl) {
          trans.videoObjectUrl = histEntry.objectUrl;
          trans.videoMimeType = histEntry.mimeType;
          trans.videoHistoryId = histEntry.id;
        } else if (hasVideoColumns) {
          // Fallback: direct BLOB (pre-migration files that somehow weren't migrated)
          const videoData = row[6] as Uint8Array | null;
          const mimeType = (row[7] as string) || 'video/mp4';
          if (videoData && videoData.length > 0) {
            const copy = new Uint8Array(videoData);
            const blob = new Blob([copy], { type: mimeType });
            trans.videoObjectUrl = URL.createObjectURL(blob);
            cacheAssetBytes(trans.videoObjectUrl, copy);
            trans.videoMimeType = mimeType;
          }
        }
        return trans;
      });
    }
  } catch {
    // Table doesn't exist in older .previs files — that's fine
  }
  return [];
}

/** Load audio clips from the extension table.
 *  `scenes` is passed in for one-time migration of legacy files that lack shotid/shotoffset. */
function loadAudioClips(db: any, scenes: TimelineScene[]): AudioClip[] {
  try {
    // Try newest schema first (shotid + shotoffset + bufferoffset), then bufferoffset-only, then minimal
    let audioResult;
    let hasShotColumns = false;
    let hasBufferOffset = false;
    try {
      audioResult = db.exec(
        `SELECT audioclipid, label, starttime, duration, volume, audiodata, mimetype,
                lane, ismuted, fadeintime, fadeouttime, color, bufferoffset, shotid, shotoffset
         FROM timeline_audioclips ORDER BY audioclipid`
      );
      hasShotColumns = true;
      hasBufferOffset = true;
    } catch {
      try {
        audioResult = db.exec(
          `SELECT audioclipid, label, starttime, duration, volume, audiodata, mimetype,
                  lane, ismuted, fadeintime, fadeouttime, color, bufferoffset
           FROM timeline_audioclips ORDER BY audioclipid`
        );
        hasBufferOffset = true;
      } catch {
        audioResult = db.exec(
          `SELECT audioclipid, label, starttime, duration, volume, audiodata, mimetype,
                  lane, ismuted, fadeintime, fadeouttime, color
           FROM timeline_audioclips ORDER BY audioclipid`
        );
      }
    }
    if (audioResult.length > 0) {
      const clips: AudioClip[] = audioResult[0].values.map((row: any[]) => {
        const clipId = row[0] as number;
        const blobBytes = row[5] as Uint8Array | null;
        // DIAG: track blob presence for later logging
        if (blobBytes) {
          stashBlob(clipId, blobBytes);
        } else {
          // eslint-disable-next-line no-console
          console.warn(`[previsLoader] DIAG: clip ${clipId} has NULL audiodata — row[5] type=${typeof row[5]} val=${row[5]}`);
        }

        // Read shotid/shotoffset from new columns (indices 13, 14) or fall back to 0/0
        const rawShotId = hasShotColumns ? (row[13] as number | null) : null;
        const rawShotOffset = hasShotColumns ? ((row[14] as number) || 0) : 0;

        return {
          id: clipId,
          label: (row[1] as string) || 'Untitled',
          shotId: rawShotId ?? 0,
          shotOffset: rawShotOffset,
          // Stash absolute starttime for migration (stripped after migration)
          _legacyStartTime: (row[2] as number) || 0,
          duration: (row[3] as number) || 0,
          volume: (row[4] as number) || 0.75,
          bufferOffset: hasBufferOffset ? ((row[12] as number) || 0) : 0,
          audioBuffer: null,
          mimeType: (row[6] as string) || 'audio/wav',
          lane: Math.min((row[7] as number) || 0, 3),
          isMuted: (row[8] as number) === 1,
          fadeInTime: (row[9] as number) || 0,
          fadeOutTime: (row[10] as number) || 0,
          color: AUDIO_LANE_COLORS[Math.min((row[7] as number) || 0, 3)],
          peaks: null,
        } as AudioClip & { _legacyStartTime: number };
      });

      // ── One-time migration: compute shotId/shotOffset from legacy starttime ──
      // Build the set of shot IDs that are actually live in scenes (Initial +
      // Normal shots; Continuing children and ishidden=1 are filtered out at
      // the SQL level above). A clip whose shotId isn't in this set is an
      // orphan — typically a TTS clip whose original shot was deleted or
      // renumbered. Previs Pro's UI silently renders orphans at frame 0 +
      // shotOffset (because getShotStartTime returns 0 for unknown ids), but
      // the NLE export drops them, and any other code that joins on shotId
      // will mis-render or skip them. Treat orphans the same as legacy clips
      // that never had a shotId at all: re-anchor via the absolute starttime.
      const validShotIds = new Set<number>();
      for (const scene of scenes) {
        for (const shot of scene.shots) validShotIds.add(shot.id);
      }
      const needsMigration = clips.some(c => !c.shotId || !validShotIds.has(c.shotId));
      if (needsMigration && scenes.length > 0) {
        // Build shot timeline: array of { shotId, absoluteStart, duration }
        const shotTimeline: { shotId: number; absoluteStart: number; duration: number }[] = [];
        let cursor = 0;
        for (const scene of scenes) {
          for (const shot of scene.shots) {
            shotTimeline.push({ shotId: shot.id, absoluteStart: cursor, duration: shot.duration });
            cursor += shot.duration;
          }
        }

        // Ensure shotid/shotoffset columns exist for the write-back
        try { db.run('ALTER TABLE timeline_audioclips ADD COLUMN shotid INTEGER'); } catch { /* already exists */ }
        try { db.run('ALTER TABLE timeline_audioclips ADD COLUMN shotoffset REAL NOT NULL DEFAULT 0'); } catch { /* already exists */ }

        for (const clip of clips) {
          if (clip.shotId && validShotIds.has(clip.shotId)) continue; // already valid
          // Best signal for the clip's intended absolute timeline position:
          // the legacy starttime column if it was non-zero, else the existing
          // shotOffset (which is what the UI was rendering for orphans, since
          // getShotStartTime returned 0 for the invalid shotId).
          const legacy = (clip as AudioClip & { _legacyStartTime: number })._legacyStartTime;
          const absTime = legacy > 0 ? legacy : clip.shotOffset;
          // Find which shot this absolute time falls within
          let anchored = false;
          for (const entry of shotTimeline) {
            if (absTime >= entry.absoluteStart && absTime < entry.absoluteStart + entry.duration) {
              clip.shotId = entry.shotId;
              clip.shotOffset = absTime - entry.absoluteStart;
              anchored = true;
              break;
            }
          }
          // Edge case: clip past the end of all shots or empty timeline → assign to last shot
          if (!anchored) {
            if (shotTimeline.length > 0) {
              const last = shotTimeline[shotTimeline.length - 1];
              clip.shotId = last.shotId;
              clip.shotOffset = Math.max(0, absTime - last.absoluteStart);
            } else {
              // Truly empty timeline — assign to first shot in scenes as fallback
              clip.shotId = scenes[0]?.shots[0]?.id ?? 0;
              clip.shotOffset = 0;
            }
          }

          // Write migrated values back to SQLite so subsequent loads (and
          // Unity's reads) see the corrected values.
          db.run(
            'UPDATE timeline_audioclips SET shotid = ?, shotoffset = ? WHERE audioclipid = ?',
            [clip.shotId, clip.shotOffset, clip.id]
          );
        }
      }

      // Strip the temporary _legacyStartTime property
      for (const clip of clips) {
        delete (clip as any)._legacyStartTime;
      }

      // // ── DIAG: Log shot timeline and clip positions for audio sync debugging ──
      // {
      //   const diagTimeline: { shotId: number; absStart: number; dur: number }[] = [];
      //   let diagCursor = 0;
      //   for (const scene of scenes) {
      //     for (const shot of scene.shots) {
      //       diagTimeline.push({ shotId: shot.id, absStart: diagCursor, dur: shot.duration });
      //       diagCursor += shot.duration;
      //     }
      //   }
      //   const shotSummary = diagTimeline.map(s => `${s.shotId}@${s.absStart.toFixed(3)}+${s.dur.toFixed(3)}`).join(', ');
      //   enqueueSendToNative('diagLog', { msg: `[AUDIO SYNC DIAG] Shot timeline (${diagTimeline.length} shots, total=${diagCursor.toFixed(3)}s): ${shotSummary}` });
      //   const clipSummary = clips.map(c => {
      //     const shotEntry = diagTimeline.find(s => s.shotId === c.shotId);
      //     const absPos = shotEntry ? shotEntry.absStart + c.shotOffset : -1;
      //     return `clip${c.id}:shot${c.shotId}+${c.shotOffset.toFixed(3)}=abs${absPos.toFixed(3)}`;
      //   }).join(', ');
      //   enqueueSendToNative('diagLog', { msg: `[AUDIO SYNC DIAG] Clip positions: ${clipSummary}` });
      // }

      return clips;
    }
  } catch {
    // Table doesn't exist — that's fine
  }
  return [];
}

/** Load comments from the extension table */
function loadComments(db: any): Comment[] {
  try {
    // Try with all columns (newest), then progressively fewer for older schemas
    let commentResult;
    let colLevel: 'userid' | 'full' | 'annotation' | 'minimal' = 'minimal';
    try {
      commentResult = db.exec(
        `SELECT commentid, shotid, timecode, author, text, createdat, resolvedat, parentcommentid, annotationsvg, screenplaypage, sceneid, userid
         FROM timeline_comments ORDER BY commentid`
      );
      colLevel = 'userid';
    } catch {
      try {
        commentResult = db.exec(
          `SELECT commentid, shotid, timecode, author, text, createdat, resolvedat, parentcommentid, annotationsvg, screenplaypage, sceneid
           FROM timeline_comments ORDER BY commentid`
        );
        colLevel = 'full';
      } catch {
        try {
          commentResult = db.exec(
            `SELECT commentid, shotid, timecode, author, text, createdat, resolvedat, parentcommentid, annotationsvg
             FROM timeline_comments ORDER BY commentid`
          );
          colLevel = 'annotation';
        } catch {
          commentResult = db.exec(
            `SELECT commentid, shotid, timecode, author, text, createdat, resolvedat, parentcommentid
             FROM timeline_comments ORDER BY commentid`
          );
        }
      }
    }
    if (commentResult.length > 0) {
      return commentResult[0].values.map((row: any[]) => ({
        id: row[0] as number,
        shotId: row[1] as number | null,
        timecode: row[2] as number | null,
        author: (row[3] as string) || '',
        text: (row[4] as string) || '',
        createdAt: (row[5] as string) || new Date().toISOString(),
        resolvedAt: (row[6] as string) || null,
        parentCommentId: (row[7] as number) || null,
        annotationSvg: colLevel !== 'minimal' ? ((row[8] as string) || null) : null,
        screenplayPage: (colLevel === 'full' || colLevel === 'userid') ? ((row[9] as number) || null) : null,
        sceneId: (colLevel === 'full' || colLevel === 'userid') ? ((row[10] as number) || null) : null,
      }));
    }
  } catch {
    // Table doesn't exist — that's fine
  }
  return [];
}

/** Load approvals from the extension table, deduplicating accumulated junk.
 *  Keeps only the highest-revision approval per shot. */
function loadApprovals(db: any): Approval[] {
  try {
    const approvalResult = db.exec(
      `SELECT approvalid, shotid, status, approvedby, approvedat, notes, revision
       FROM timeline_approvals ORDER BY approvalid`
    );
    if (approvalResult.length > 0) {
      const all: Approval[] = approvalResult[0].values.map((row: any[]) => ({
        id: row[0] as number,
        shotId: row[1] as number,
        status: normalizeApprovalStatus((row[2] as string) || 'pending'),
        approvedBy: (row[3] as string) || '',
        approvedAt: (row[4] as string) || new Date().toISOString(),
        notes: (row[5] as string) || '',
        revision: (row[6] as number) || 1,
      }));
      // Dedup: keep only the entry with the highest revision per shot.
      // Multiple entries per (shotId, revision) can accumulate from
      // local/remote ID mismatches during sync cycles.
      const bestByShot = new Map<number, Approval>();
      for (const a of all) {
        const existing = bestByShot.get(a.shotId);
        if (!existing || a.revision > existing.revision) {
          bestByShot.set(a.shotId, a);
        }
      }
      return Array.from(bestByShot.values());
    }
  } catch {
    // Table doesn't exist — that's fine
  }
  return [];
}

/** Parse `screenplay_dialogue.character_ids_json` — a JSON int array (`"[3,7]"`) written whenever a
 *  line's speakers are set, `''` on rows that predate that write. Anything unreadable yields an
 *  empty list, which sends the caller back to the primary `character_id`. */
function parseCharacterIdsJson(raw: unknown): number[] {
  if (typeof raw !== 'string' || raw.trim() === '') return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((n): n is number => typeof n === 'number' && Number.isFinite(n));
  } catch {
    return [];
  }
}

/** Load screenplay text + structured screenplay (dialogue, actions, transitions, characters, locations) */
export function loadScreenplayData(
  db: any,
  scenes: TimelineScene[],
): { characters: ScreenplayCharacter[]; locations: ScreenplayLocation[] } {
  // ── Screenplay text (core table — may not exist in older files) ───
  try {
    const screenplayResult = db.exec(
      'SELECT sceneid, text FROM screenplay'
    );
    if (screenplayResult.length > 0) {
      const textMap = new Map<number, string>();
      for (const row of screenplayResult[0].values) {
        textMap.set(row[0] as number, row[1] as string);
      }
      for (const scene of scenes) {
        scene.screenplayText = textMap.get(scene.id);
      }
    }
  } catch {
    // Table doesn't exist in older .previs files — that's fine
  }

  // ── Structured screenplay (v41+ tables — may not exist) ─────────────
  const characterNameMap = new Map<number, string>();
  const characters: ScreenplayCharacter[] = [];
  try {
    let charResult;
    // Tiered by file version: v53 adds cast_placeholder, v50 added the dossier/cast columns.
    let charColLevel: 'placeholder' | 'cast' | 'minimal' = 'minimal';
    // `customcharacterid` (the cast row's link to a custom body) predates every tier below, but it
    // gets a tier of its own anyway: it is not worth dragging cast_placeholder — which gates the
    // whole cast pipeline — down a tier because one hand-built or slimmed DB lacks it. Losing it
    // only costs the Script tab its EDIT CHARACTER / CUSTOM MODEL split, which fails closed on
    // CUSTOM MODEL.
    let hasCustomCharacterId = true;
    try {
      charResult = db.exec('SELECT characterid, name, gendertype, appearance_desc, cast_bundled_id, cast_placeholder, customcharacterid FROM characters');
      charColLevel = 'placeholder';
    } catch (customIdErr) {
      hasCustomCharacterId = false;
      try {
        charResult = db.exec('SELECT characterid, name, gendertype, appearance_desc, cast_bundled_id, cast_placeholder FROM characters');
        charColLevel = 'placeholder';
      } catch (placeholderErr) {
        try {
          charResult = db.exec('SELECT characterid, name, gendertype, appearance_desc, cast_bundled_id FROM characters');
          charColLevel = 'cast';
          // Logged AFTER the lower tier succeeds, so a file with no `characters` table at all (pre-v41)
          // falls through to the outer catch silently instead of emitting a fallback line per tier.
          // The catch above is blind — the realistic cause is a pre-v53 file, but ANY exec failure lands
          // here and costs every row its castPlaceholder, which switches the cast pipeline off wholesale.
          // Unconditional send: the file-local diagLog() helper is gated behind DIAG_HISTORY_LOGGING.
          enqueueSendToNative('diagLog', { msg: `[previsLoader] characters read at the v50 tier — cast_placeholder unreadable, so castPlaceholder is undefined for every row: ${placeholderErr}` });
        } catch (castErr) {
          charResult = db.exec('SELECT characterid, name, gendertype FROM characters'); // pre-v50 file
          enqueueSendToNative('diagLog', { msg: `[previsLoader] characters read at the pre-v50 tier — appearance_desc / cast_bundled_id / cast_placeholder all unreadable: ${castErr}` });
        }
      }
      // Same after-the-fact placement as the two lines above, and for the same reason: a file with
      // no `characters` table throws out of every tier and must stay silent.
      enqueueSendToNative('diagLog', { msg: `[previsLoader] characters read without customcharacterid — every cast row falls back to the Script tab's CUSTOM MODEL button: ${customIdErr}` });
    }
    // The slim board DB carries `customcharacters` values-only (customcharacterid + avatar_recipe +
    // name, every blob column substituted), so this is a cheap read there and a real one in a
    // standalone .previs. Absent entirely on older slim builds and pre-v49 files — every row then
    // reads hasRecipe:false, which is the safe answer (offer the replace flow, not an edit that
    // cannot open).
    const recipePresentByCustomId = new Map<number, boolean>();
    try {
      const recipeResult = db.exec('SELECT customcharacterid, avatar_recipe FROM customcharacters');
      if (recipeResult.length > 0) {
        for (const row of recipeResult[0].values) {
          const recipe = row[1];
          recipePresentByCustomId.set(Number(row[0]), typeof recipe === 'string' && recipe.trim() !== '');
        }
      }
    } catch { /* no customcharacters table (older slim DB / pre-v49 file) — every row reads false */ }
    if (charResult.length > 0) {
      for (const row of charResult[0].values) {
        const id = row[0] as number;
        const name = (row[1] as string) || '';
        characterNameMap.set(id, name);
        // 0 is the column's "no custom body" default, so it is dropped rather than carried: an
        // absent customCharacterId and a 0 mean the same thing to every reader.
        const customCharacterId = hasCustomCharacterId ? Number(row[6]) || 0 : 0;
        characters.push({
          id, name,
          gender: (row[2] as string) || '',
          appearance: (row[3] as string) || undefined,
          castBundledId: (row[4] as string) || undefined,
          // Left UNDEFINED below the top tier on purpose: a pre-v53 file cannot answer "is this row
          // awaiting a pick", and undefined must not read as false — see ScreenplayCharacter.
          ...(charColLevel === 'placeholder' ? { castPlaceholder: Number(row[5]) === 1 } : {}),
          ...(customCharacterId > 0
            ? { customCharacterId, hasRecipe: recipePresentByCustomId.get(customCharacterId) === true }
            : {}),
        });
      }
    }
    // [CastTrace] (20260902) — the roster as the web will hand it to the cast pipeline, from the file the
    // host actually pushed. The two lower tiers already log; this is the top tier's line, so a device log
    // can tell "the slim DB carried no flagged rows" from "the pipeline never ran".
    if (charColLevel === 'placeholder') {
      const flagged = characters.filter((c) => c.castPlaceholder === true).length;
      const awaiting = characters.filter((c) => c.castPlaceholder === true && (!c.appearance || !c.castBundledId)).length;
      enqueueSendToNative('diagLog', { tag: 'CastTrace', msg: `[previsLoader] characters: ${characters.length} rows at the v53 tier — ${flagged} flagged cast_placeholder=1, ${awaiting} awaiting a pick` });
    }
  } catch { /* table doesn't exist in older files */ }

  const locations: ScreenplayLocation[] = [];
  let locResult: ReturnType<typeof db.exec> = [];
  let hasV52Cols = true;
  try {
    locResult = db.exec(
      'SELECT screenplay_location_id, name, interior_exterior, description, llm_blocking, set_key, set_variation_key FROM screenplay_locations'
    );
  } catch {
    hasV52Cols = false;
    try {
      locResult = db.exec(
        'SELECT screenplay_location_id, name, interior_exterior FROM screenplay_locations'
      );
    } catch { /* table doesn't exist (pre-v41 file) */ }
  }
  if (locResult.length > 0) {
    for (const row of locResult[0].values) {
      locations.push({
        id: row[0] as number,
        name: (row[1] as string) || '',
        interiorExterior: (row[2] as string) || '',
        ...(hasV52Cols ? {
          description: (row[3] as string) || undefined,
          llmBlocking: (row[4] as string) || undefined,
          setKey: (row[5] as string) || undefined,
          setVariationKey: (row[6] as string) || undefined,
        } : {}),
      });
    }
  }

  // Load structured dialogue, actions, transitions — grouped by scene
  if (tableExists(db, 'screenplay_dialogue')) {
    const dialogueByScene = new Map<number, ScreenplayDialogue[]>();
    const actionsByScene = new Map<number, ScreenplayAction[]>();
    const transitionsByScene = new Map<number, ScreenplayTransitionElement[]>();

    try {
      // Tiered like the `characters` SELECT above: character_ids_json is the multi-speaker record
      // (Unity keeps the ids in pick order, with character_id repeating the first), and without it
      // every secondary speaker is dropped on reload even though the file still holds them. A file
      // slim enough to lack the column reads at the lower tier and keeps the primary-only behavior.
      let dlgResult;
      let hasIdsJson = true;
      try {
        dlgResult = db.exec(
          `SELECT screenplay_dialogue_id, scene_id, shot_id, character_id,
                  dialogue_text, parenthetical, extension, is_dual_dialogue, script_order,
                  character_ids_json
           FROM screenplay_dialogue ORDER BY script_order`
        );
      } catch {
        hasIdsJson = false;
        dlgResult = db.exec(
          `SELECT screenplay_dialogue_id, scene_id, shot_id, character_id,
                  dialogue_text, parenthetical, extension, is_dual_dialogue, script_order
           FROM screenplay_dialogue ORDER BY script_order`
        );
      }
      if (dlgResult.length > 0) {
        for (const row of dlgResult[0].values) {
          const sceneId = row[1] as number;
          const charId = row[3] as number;
          // De-duplicated: Unity's cast merge rewrites the array in place, replacing the source id
          // with the target's, so a line assigned to both ends up holding the target twice — which
          // would read back as "MISSY / MISSY". Dedupe on the NAME, which also collapses two
          // distinct roster rows that happen to share one.
          const speakerNames = hasIdsJson
            ? [...new Set(parseCharacterIdsJson(row[9]).map(id => characterNameMap.get(id) || '').filter(Boolean))]
            : [];
          const entry: ScreenplayDialogue = {
            id: row[0] as number,
            sceneId,
            shotId: (row[2] as number) || 0,
            characterId: charId,
            // Same " / " join the editor writes, so a reloaded multi-speaker line matches under
            // each of its speakers exactly as it did before the reload.
            characterName: speakerNames.length > 0
              ? speakerNames.join(' / ')
              : characterNameMap.get(charId) || '',
            dialogueText: (row[4] as string) || '',
            parenthetical: (row[5] as string) || '',
            extension: (row[6] as string) || '',
            isDualDialogue: (row[7] as number) === 1,
            scriptOrder: (row[8] as number) || 0,
          };
          const list = dialogueByScene.get(sceneId);
          if (list) list.push(entry);
          else dialogueByScene.set(sceneId, [entry]);
        }
      }
    } catch { /* query failed */ }

    try {
      const actResult = db.exec(
        `SELECT screenplay_action_id, scene_id, shot_id, action_text, script_order
         FROM screenplay_actions ORDER BY script_order`
      );
      if (actResult.length > 0) {
        for (const row of actResult[0].values) {
          const sceneId = row[1] as number;
          const entry: ScreenplayAction = {
            id: row[0] as number,
            sceneId,
            shotId: (row[2] as number) || 0,
            actionText: (row[3] as string) || '',
            scriptOrder: (row[4] as number) || 0,
          };
          const list = actionsByScene.get(sceneId);
          if (list) list.push(entry);
          else actionsByScene.set(sceneId, [entry]);
        }
      }
    } catch { /* query failed */ }

    try {
      const transResult = db.exec(
        `SELECT screenplay_transition_id, scene_id, transition_text, script_order
         FROM screenplay_transitions ORDER BY script_order`
      );
      if (transResult.length > 0) {
        for (const row of transResult[0].values) {
          const sceneId = row[1] as number;
          const entry: ScreenplayTransitionElement = {
            id: row[0] as number,
            sceneId,
            transitionText: (row[2] as string) || '',
            scriptOrder: (row[3] as number) || 0,
          };
          const list = transitionsByScene.get(sceneId);
          if (list) list.push(entry);
          else transitionsByScene.set(sceneId, [entry]);
        }
      }
    } catch { /* query failed */ }

    // Attach ScreenplayData to scenes that have structured data
    for (const scene of scenes) {
      const dlg = dialogueByScene.get(scene.id);
      const act = actionsByScene.get(scene.id);
      const trans = transitionsByScene.get(scene.id);
      if ((dlg && dlg.length > 0) || (act && act.length > 0) || (trans && trans.length > 0)) {
        const data: ScreenplayData = {
          dialogue: dlg || [],
          actions: act || [],
          transitions: trans || [],
        };
        scene.screenplay = data;
      }
    }
  }

  return { characters, locations };
}

/** Result type for loadExtensionMeta */
interface ExtensionMeta {
  reviewSessionId: string | null;
  aiAudioHints: Record<number, string>;
  voiceAssignments: VoiceAssignment[];
}

/** Load review session ID, AI audio hints, and voice assignments from extension tables */
function loadExtensionMeta(db: any): ExtensionMeta {
  // ── Review session ID ────────────
  let reviewSessionId: string | null = null;
  try {
    const metaResult = db.exec('SELECT session_id FROM timeline_review_meta LIMIT 1');
    if (metaResult.length > 0 && metaResult[0].values.length > 0) {
      reviewSessionId = (metaResult[0].values[0][0] as string) || null;
    }
  } catch {
    // Table doesn't exist — that's fine
  }

  // ── Scene audio hints ────────────
  const aiAudioHints: Record<number, string> = {};
  try {
    const hintRows = db.exec('SELECT sceneid, hint FROM timeline_scene_audio_hints');
    if (hintRows.length > 0) {
      for (const row of hintRows[0].values) {
        const sceneId = row[0] as number;
        const hint = (row[1] as string) || '';
        if (hint) aiAudioHints[sceneId] = hint;
      }
    }
  } catch { /* table doesn't exist in older files */ }

  // ── Voice assignments ────────────
  const voiceAssignments: VoiceAssignment[] = [];
  try {
    const vaRows = db.exec('SELECT sceneid, character, model_id, voice FROM timeline_voice_assignments');
    if (vaRows.length > 0) {
      for (const row of vaRows[0].values) {
        voiceAssignments.push({
          sceneId: row[0] as number,
          character: (row[1] as string) || '',
          modelId: (row[2] as string) || 'elevenlabs',
          voice: (row[3] as string) || '',
        });
      }
    }
  } catch { /* table doesn't exist in older files */ }

  return { reviewSessionId, aiAudioHints, voiceAssignments };
}

/** Convert Unity sketch overlays on shots into Timeline annotation comments */
function convertSketchOverlays(db: any, comments: Comment[]): void {
  try {
    if (!columnExists(db, 'shots', 'hassketchoverlay')) return;

    const result = db.exec(
      'SELECT shotid, sketchoverlaydefinition FROM shots WHERE hassketchoverlay = 1'
    );
    if (result.length === 0) return;

    // Deduplicate: skip shots that already have the imported comment
    const IMPORT_TEXT = '[Sketch overlay imported from Previs Pro]';
    const importedShotIds = new Set(
      comments
        .filter(c => c.text === IMPORT_TEXT)
        .map(c => c.shotId),
    );

    let nextId = comments.reduce((m, c) => Math.max(m, c.id), 0) + 1;

    for (const row of result[0].values) {
      const shotId = row[0] as number;
      const sketchJson = row[1] as string;

      if (!sketchJson || importedShotIds.has(shotId)) continue;

      const annotationData = sketchOverlayToAnnotation(sketchJson);
      if (!annotationData) continue;

      comments.push({
        id: nextId++,
        shotId,
        timecode: null,
        author: 'Previs Pro',
        text: IMPORT_TEXT,
        createdAt: new Date().toISOString(),
        resolvedAt: null,
        parentCommentId: null,
        annotationSvg: JSON.stringify(annotationData),
      });
    }
  } catch {
    // Column/table doesn't exist in older files — that's fine
  }
}

/** Fill in default pending approvals for shots that don't have one */
function ensureDefaultApprovals(scenes: TimelineScene[], approvals: Approval[]): void {
  const coveredShotIds = new Set(approvals.map(a => a.shotId));
  let nextApprovalId = approvals.reduce((m, a) => Math.max(m, a.id), 0) + 1;
  for (const scene of scenes) {
    for (const shot of scene.shots) {
      if (!coveredShotIds.has(shot.id)) {
        approvals.push({
          id: nextApprovalId++,
          shotId: shot.id,
          status: 'pending',
          approvedBy: '',
          approvedAt: new Date().toISOString(),
          notes: '',
          revision: 1,
        });
      }
    }
  }
}

// ─── Baseline ─────────────────────────────────────────────────────────────

/** Load the latest screenplay import baseline from SQLite (if present). */
function loadBaseline(db: any): BaselinePayload | undefined {
  if (!tableExists(db, 'screenplay_import_baseline')) return undefined;
  try {
    const importResult = db.exec(
      'SELECT import_id, import_date, source_filename, version_label, version_number FROM screenplay_import_baseline ORDER BY import_id DESC LIMIT 1'
    );
    if (!importResult.length || !importResult[0].values.length) return undefined;
    const row = importResult[0].values[0];
    const importId = row[0] as number;

    const scenesResult = db.exec(
      'SELECT baseline_scene_id, import_id, original_scene_id, slugline, scene_number, script_order FROM screenplay_baseline_scenes WHERE import_id = ? ORDER BY script_order',
      [importId]
    );
    const scenes: BaselineScene[] = [];
    if (scenesResult.length > 0) {
      for (const sr of scenesResult[0].values) {
        scenes.push({
          baselineSceneId: sr[0] as number,
          importId: sr[1] as number,
          originalSceneId: sr[2] as number,
          slugline: (sr[3] as string) || '',
          sceneNumber: (sr[4] as string) || '',
          scriptOrder: sr[5] as number,
        });
      }
    }

    const elementsResult = db.exec(
      'SELECT baseline_element_id, baseline_scene_id, element_type, character_name, dialogue_text, action_text, transition_text, parenthetical, script_order FROM screenplay_baseline_elements WHERE baseline_scene_id IN (SELECT baseline_scene_id FROM screenplay_baseline_scenes WHERE import_id = ?) ORDER BY baseline_scene_id, script_order',
      [importId]
    );
    const elements: BaselineElement[] = [];
    if (elementsResult.length > 0) {
      for (const er of elementsResult[0].values) {
        elements.push({
          baselineElementId: er[0] as number,
          baselineSceneId: er[1] as number,
          elementType: (er[2] as string) || '',
          characterName: (er[3] as string) || '',
          dialogueText: (er[4] as string) || '',
          actionText: (er[5] as string) || '',
          transitionText: (er[6] as string) || '',
          parenthetical: (er[7] as string) || '',
          scriptOrder: er[8] as number,
        });
      }
    }

    return {
      importId,
      importDate: (row[1] as string) || '',
      sourceFilename: (row[2] as string) || '',
      versionLabel: (row[3] as string) || '',
      versionNumber: (row[4] as number) || 1,
      scenes,
      elements,
    };
  } catch {
    return undefined;
  }
}

// ─── Main Entry Point ──────────────────────────────────────────────────────

export interface LoadPrevisFileOptions {
  /**
   * Skip grafting a shot's generation-history image onto its `thumbnailDataUrl`
   * (resolveHistoryToShots' image branch). Embedded mode only, set at the bridge
   * call site — never derived in here.
   *
   * 20260825 - The graft is the tile authority in standalone/review, where the
   * history BLOB is the ONLY pixel source a shot has. Embedded is the opposite:
   * Unity owns every board tile (the cropped `{shotId}.jpg` it pushes on
   * sceneReady/thumbnailChanged) and the slim DB ships `shots.staticimagedata`
   * as an empty blob, so the graft's only effect there is to put the RAW,
   * UNCROPPED model output on the tile — beating the carried-over correct tile
   * (sqliteBridge's `fresh ?? mem`) on every canvas-exit repush until Unity's
   * sceneReady burst corrects it. That is the "correct → old/uncropped → correct"
   * flash. History ENTRIES and their objectUrls still load either way; the
   * generation-history UI depends on them.
   */
  suppressHistoryTileGraft?: boolean;
}

export async function loadPrevisFile(buffer: ArrayBuffer, opts?: LoadPrevisFileOptions): Promise<TimelineProject> {
  clearAssetByteCache();
  clearPersistedHistoryTracking();

  const db = await openDatabase(new Uint8Array(buffer));

  try {
    const { title, aspectRatio, sensorSize, lastModified } = loadDocumentMetadata(db);
    const fileVersion = loadFileVersion(db);
    const documentSettings = loadDocumentSettings(db);
    const frameRate = loadProjectFrameRate(db);
    const { scenes, revertedShotIds } = loadScenesWithShots(db);

    // Apply computed numbering from document settings, or simple index fallback for old files
    if (documentSettings) {
      if (documentSettings.isNumberingLocked) {
        // Locked: Unity wrote computed labels to DB — use as-is.
        // Only fill in scenes that are still empty (shouldn't happen, but safe).
        const sceneNumbers = computeSceneNumbers(scenes, documentSettings.startingSceneNumber);
        for (let i = 0; i < scenes.length; i++) {
          if (!scenes[i].number) scenes[i].number = sceneNumbers[i];
        }
      } else {
        // Unlocked: compute on the fly — empty = auto, non-empty = manual override
        const sceneNumbers = computeSceneNumbers(scenes, documentSettings.startingSceneNumber);
        for (let i = 0; i < scenes.length; i++) {
          scenes[i].number = sceneNumbers[i];
          const shotNumbers = computeShotNumbers(
            scenes[i].shots,
            documentSettings.useFullAlphabet,
            documentSettings.useDigitsForShots,
          );
          for (let j = 0; j < scenes[i].shots.length; j++) {
            scenes[i].shots[j].number = shotNumbers[j];
          }
        }
      }
    } else {
      // No documentsettings table — should only happen with very old files
      enqueueSendToNative('diagLog', { msg: '[previsLoader] WARNING: documentSettings unavailable — falling back to order-based numbering' });
      for (let i = 0; i < scenes.length; i++) {
        if (!scenes[i].number) scenes[i].number = `${i + 1}`;
        for (const shot of scenes[i].shots) {
          if (!shot.number) shot.number = `${shot.order}`;
        }
      }
    }

    migrateToHistoryTable(db, scenes);

    const { entries: generationHistory, byId: historyById, videoByShot, videoByTransition } =
      loadGenerationHistory(db);
    const videoShots = resolveHistoryToShots(
      scenes, historyById, videoByShot, revertedShotIds,
      opts?.suppressHistoryTileGraft === true,
    );
    const transitions = loadTransitions(db, videoByTransition);
    // Clear previous project's blobs BEFORE loading new ones
    clearAllBlobs();

    const audioClips = loadAudioClips(db, scenes);
    const comments = loadComments(db);
    convertSketchOverlays(db, comments);
    const rawApprovals = loadApprovals(db);
    // Clean up orphaned references whose shots no longer exist (deleted in Unity)
    const validShotIds = new Set(scenes.flatMap(s => s.shots.map(sh => sh.id)));
    const approvals = rawApprovals.filter(a => validShotIds.has(a.shotId));
    // Remove comments pointing to deleted shots (preserve global: shotId null/0)
    const rawCommentCount = comments.length;
    for (let i = comments.length - 1; i >= 0; i--) {
      const sid = comments[i].shotId;
      if (sid != null && sid !== 0 && !validShotIds.has(sid)) {
        comments.splice(i, 1);
      }
    }
    if (comments.length < rawCommentCount && DIAG_HISTORY_LOGGING) {
      console.log(`[previsLoader] Removed ${rawCommentCount - comments.length} orphaned comments`);
    }
    // Detach history entries whose shot was deleted (show as "Detached" in panel)
    for (const entry of generationHistory) {
      if (entry.shotId != null && !validShotIds.has(entry.shotId)) {
        entry.shotId = null;
      }
    }
    const { characters: screenplayCharacters, locations: screenplayLocations } =
      loadScreenplayData(db, scenes);
    const { reviewSessionId, aiAudioHints, voiceAssignments } = loadExtensionMeta(db);
    const baseline = loadBaseline(db);

    ensureDefaultApprovals(scenes, approvals);

    const totalDuration = scenes.reduce(
      (sum, scene) => scene.isOmitted ? sum : sum + scene.shots.reduce((s, shot) => s + shot.duration, 0),
      0
    );
    const hasScreenplay = scenes.some(s => !!s.screenplayText || !!s.screenplay);

    return {
      title, aspectRatio, sensorSize, frameRate, scenes, totalDuration, transitions,
      audioClips, videoShots, comments, approvals, hasScreenplay,
      reviewSessionId, aiAudioHints, generationHistory,
      voiceAssignments, screenplayLocations, screenplayCharacters,
      documentSettings, lastModified, baseline, fileVersion,
    };
  } finally {
    closeDatabase(db);
  }
}
